import type { ReactElement, ReactNode } from "react"
import { useId } from "react"
import _t from "@core/i18n"
import type { PeriodInfo } from "@core/period-info"
import {
  Checkbox,
  CheckboxIndicator,
  CheckboxLabel,
  Text,
  Tooltip,
} from "@ionos-web-design-system/react"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { productBoxClickEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"

type ProductBoxFooterProps = {
  label: string
  value?: string
  tooltip?: ReactNode
  setIsChecked: () => void
  bundlePriceFormat?: string
  isChecked: boolean
  isBundle?: boolean
  periodInfo?: PeriodInfo | null
}

export default function ProductBoxFooter({
  label,
  value,
  tooltip,
  setIsChecked,
  bundlePriceFormat,
  isChecked,
  isBundle,
  periodInfo,
}: ProductBoxFooterProps): ReactElement<ProductBoxFooterProps> | null {
  const { isNettoPriceType } = usePriceTypeContext()
  const checkboxId = useId()

  const onChange = () => {
    if (!isChecked) {
      productBoxClickEvent({
        productbox_type: "checkbox",
        productbox_option: label,
      })

      window.jentisDataLayer?.pushProductBoxClickEvent({
        productbox_type: "checkbox",
        productbox_option: label,
      })
    }

    setIsChecked()
  }

  const priceTypeText =
    isEmpty(value) && !isEmpty(periodInfo)
      ? isNettoPriceType
        ? _t("prices.netto")
        : _t("prices.brutto")
      : undefined

  const periodText =
    isEmpty(value) && !isEmpty(periodInfo)
      ? `${_t(`periodName.searchDomainPrice.3`, {
          smart_count: periodInfo?.getPeriodLength(),
        })}`
      : undefined

  const labelContentElement = tooltip ? (
    <Tooltip content={<span className="text-base-inverted">{tooltip}</span>}>
      <Text
        variant="bodySm"
        color="base"
        tabIndex={0}
        className="cursor-help underline decoration-dashed"
      >
        {label}
      </Text>
    </Tooltip>
  ) : (
    <Text variant="bodySm" color="base">
      {label}
    </Text>
  )

  return (
    <div className="flex justify-between items-center gap-x-gap-8 gap-y-gap-12 flex-wrap p-3 rounded-xs bg-surface-subtle border border--base">
      <div className="flex items-center gap-gap-14">
        {isBundle ? (
          <>
            <Checkbox
              id={checkboxId}
              checked={isChecked}
              onCheckedChange={onChange}
              className="product-box-checkbox data-[state=unchecked]:hover:bg-[#9CAABA]! data-[state=checked]:bg-(--border-input-root-hover-on)! border-(--border-input-root-hover-on)!"
            >
              <CheckboxIndicator />
            </Checkbox>

            <CheckboxLabel htmlFor={checkboxId}>
              {labelContentElement}
            </CheckboxLabel>
          </>
        ) : (
          labelContentElement
        )}
      </div>

      <Text
        variant="bodySm"
        weight="black"
        alignment="right"
        color="promotion"
        className="ml-auto"
      >
        {!isEmpty(value)
          ? value
          : isBundle && !isEmpty(bundlePriceFormat)
            ? `+ ${bundlePriceFormat}`
            : null}

        {!isEmpty(priceTypeText) ? ` ${priceTypeText}` : null}

        {!isEmpty(periodText) ? (
          <>
            {" "}
            <span className="text-base font-normal">{periodText}</span>
          </>
        ) : null}
      </Text>
    </div>
  )
}
